import { useEffect, useRef } from "react";
import useForm from "./useForm";

/**
 * Custom hook for registering form field validations
 * Automatically handles registration and cleanup
 *
 * @param {string} fieldName - Name of the form field to validate
 * @param {Function} validationFn - Validation function that receives (formValues, event, fieldName) and returns boolean
 * @param {Array} dependencies - Dependencies array for the validation function (similar to useEffect)
 *
 * @example
 * useFormValidation("birthNumber", (formValues) => {
 *   return formValues.birthNumber?.length > 0;
 * }, []);
 *
 * @example
 * useFormValidation("email", (formValues) => {
 *   return formValues.email?.includes("@");
 * }, [someOtherValue]);
 */
function useFormValidation(fieldName, validationFn, dependencies = []) {
  const form = useForm();
  const validationFnRef = useRef(validationFn);

  // Keep the validation function ref up to date
  useEffect(() => {
    validationFnRef.current = validationFn;
  }, [validationFn]);

  useEffect(() => {
    const unregister = form.registerValidation(fieldName, (...args) => validationFnRef.current(...args));
    return unregister;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, fieldName, ...dependencies]);
}
export default useFormValidation;
